/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, ApiService } from '@app/@core/services';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';

@Component({
  selector: 'comments-widget',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss']
})
export class CommentsWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('commentInput', {static: false}) commentInput: ElementRef;
  @ViewChild('socialContainer', {static: false}) socialContainer: ElementRef;
  @ViewChild('commentsContainer', {static: false}) commentsContainer: ElementRef;
  @ViewChild('commentSection', {static: false}) commentSection: ElementRef;

  private dataSub: Subscription;
  private addCommentSub: Subscription;
  private deleteCommentSub: Subscription;
  private likeUnlikeSub: Subscription;

  @Input() id: string = null;
  @Input() parent: string = null;
  @Input('parent-id') parentId: string = null;

  @Input('like') like_enabled: boolean = true;
  @Input() condensed: boolean = false;
  
  @Input() show: boolean = false;

  now = new Date();
  reactions_url: string = null;
  comments = [];
  comments_url: string = null;
  comments_pagination = {
    'current_page': 0,
    'total_pages': 0,
    'total': 0,
    'likes': 0,
    'liked': false
  };
  comments_show: boolean = false;
  comments_loaded: boolean = false;
  comments_processing: boolean = false;
  comments_form = this.formBuilder.group({
    comment: '',
  });
  get comment_input() { return this.comments_form.controls.comment; }

  constructor(
    private apiService: ApiService,
    public authService: AuthService,
    private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.comments_url = 'comments/' + this.parent + '/' + this.parentId;
    this.reactions_url = 'reactions/' + this.parent + '/' + this.parentId;
    this.getCommentsCount();
  }

  ngOnDestroy() {
    if (this.dataSub) this.dataSub.unsubscribe();
    if (this.addCommentSub) this.addCommentSub.unsubscribe();
    if (this.deleteCommentSub) this.deleteCommentSub.unsubscribe();
    if (this.likeUnlikeSub) this.likeUnlikeSub.unsubscribe();
  }

  inputFocus() {
    this.commentInput.nativeElement.focus();
  }

  getCommentsCount() {
    this.dataSub = this.apiService.get(this.comments_url + '/count').subscribe((result) => {
      if (result.error === false) {
        this.comments_pagination['total'] = result.comments;
        this.comments_pagination['likes'] = result.likes;
        this.comments_pagination['liked'] = result.liked;

        if (this.show && result.comments>0) {
          this.showComments();
        }
      }
    });
  }

  getComments() {
    if (this.comments_pagination['total_pages'] == 0 || this.comments_pagination['current_page'] < this.comments_pagination['total_pages']) {
      this.comments_pagination['current_page']++;
      this.dataSub = this.apiService.get(this.comments_url + '?page=' + this.comments_pagination['current_page']).subscribe((result) => {
          if (result.error === false && result.data.length > 0) {
            this.comments_pagination = {
              'total': result['page_info']['total'],
              'likes': result['page_info']['likes'],
              'current_page': result['page_info']['current_page'],
              'total_pages': result['page_info']['total_pages'],
              'liked': result['page_info']['liked'],
            };
            result.data.forEach(comment => {
              let uuid = comment.uuid;
              if (this.comments.filter(item => (item.uuid == uuid)).length === 0) this.comments.unshift(comment);
            });
          }
          this.comments_loaded = true;
          setTimeout(() => this.socialContainer.nativeElement.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"}));
        });
    }
  }

  showComments() {
    if (!this.comments_show) {
      this.comments_show = true;
      this.getComments();
    }
  }

  resetComment() {
    if (!this.comments_processing) this.comment_input.reset();
  }

  submitComment() {
    if (!this.comments_processing) {
      this.comments_processing = true;
      this.addCommentSub = this.apiService.post(this.comments_url, {'content': this.comment_input.value}).subscribe(res => {
        if (res.error === false) {
          this.comment_input.reset();
          this.comments_pagination['total']++;
          this.comments.push(res.data);
        }
        this.comments_show = true;
        this.comments_processing = false;
        this.comments_loaded = true;
        setTimeout(() => this.commentSection.nativeElement.scrollIntoView({behavior: "smooth", block: "end", inline: "nearest"}));
      });
    }
  }

  deleteComment(comment) {
    console.log(comment);
    if (!this.comments_processing) {
      this.comments_processing = true;
      this.deleteCommentSub = this.apiService.delete('comments/' + comment.uuid).subscribe(result => {
        if (result.error === false && result.data.length > 0) {
          this.comments_pagination = {
            'total': result['page_info']['total'],
            'likes': result['page_info']['likes'],
            'current_page': result['page_info']['current_page'],
            'total_pages': result['page_info']['total_pages'],
            'liked': result['page_info']['liked'],
          };
          result.data.forEach(comment => {
            let uuid = comment.uuid;
            if (this.comments.filter(item => (item.uuid == uuid)).length === 0) this.comments.unshift(comment);
          });
        }
        this.comments_loaded = true;
        setTimeout(() => this.socialContainer.nativeElement.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"}));
      });
    }
  }

  toggleLike() {
    let type = null;
    if (this.comments_pagination['liked'] === true) type = 'unlike';
    else type = 'like';

    this.likeUnlikeSub = this.apiService.post(this.reactions_url + '/' + type).subscribe(res => {
      if (res.error === false) {
        this.comments_pagination['likes'] = res.likes;
        this.comments_pagination['liked'] = res.liked;
      }
    });
  }
}
